import i18n from "@/i18n";

export interface TranslationImportSuccess {
  ok: true;
  keys: Record<string, string>;
  importedCount: number;
  missingRequiredKeys: string[];
  unknownKeys: string[];
  invalidValueKeys: string[];
}

export interface TranslationImportError {
  ok: false;
  message: string;
}

export type TranslationImportResult =
  | TranslationImportSuccess
  | TranslationImportError;

export function parseTranslationJsonImport(
  rawJson: string,
  requiredKeys: string[],
): TranslationImportResult {
  let parsed: unknown;

  try {
    parsed = JSON.parse(rawJson) as unknown;
  } catch {
    return { ok: false, message: i18n.t("translationImport.invalidJson") };
  }

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return {
      ok: false,
      message: i18n.t("translationImport.expectedKeysObject"),
    };
  }

  const parsedRecord = parsed as { keys?: unknown };
  const sourceKeys = parsedRecord.keys;
  if (!sourceKeys || typeof sourceKeys !== "object" || Array.isArray(sourceKeys)) {
    return {
      ok: false,
      message: i18n.t("translationImport.expectedKeysObjectInFile"),
    };
  }

  const requiredKeySet = new Set(requiredKeys);
  const nextKeys: Record<string, string> = Object.fromEntries(
    requiredKeys.map((key) => [key, ""]),
  );
  const unknownKeys: string[] = [];
  const invalidValueKeys: string[] = [];
  let importedCount = 0;

  for (const [key, value] of Object.entries(sourceKeys)) {
    if (!requiredKeySet.has(key)) {
      unknownKeys.push(key);
      continue;
    }
    if (typeof value !== "string") {
      invalidValueKeys.push(key);
      continue;
    }
    nextKeys[key] = value;
    importedCount += 1;
  }

  const missingRequiredKeys = requiredKeys.filter(
    (key) => (nextKeys[key] ?? "").trim() === "",
  );

  return {
    ok: true,
    keys: nextKeys,
    importedCount,
    missingRequiredKeys,
    unknownKeys,
    invalidValueKeys,
  };
}